import { type RefObject, useEffect } from 'react'

const HINT_DELAY_MS = 900
const MOBILE_QUERY = '(max-width: 767px)'
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

// Eases out to the peek, springs back past the start, then settles: one motion, no hard stops.
const HINT_KEYFRAMES: Keyframe[] = [
  { transform: 'translateX(0)', easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  { transform: 'translateX(-48px)', offset: 0.4, easing: 'cubic-bezier(0.45, 0, 0.55, 1)' },
  { transform: 'translateX(8px)', offset: 0.75, easing: 'cubic-bezier(0.33, 1, 0.68, 1)' },
  { transform: 'translateX(0)' },
]
const HINT_DURATION_MS = 1200

/** Bounces a horizontally scrolling row's items once after load, on mobile, so its hidden options read as swipeable. */
export function useScrollHint(rowRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const row = rowRef.current
    if (!row || !window.matchMedia(MOBILE_QUERY).matches || window.matchMedia(REDUCED_MOTION_QUERY).matches) {
      return
    }

    let animations: Animation[] = []
    // The items move, not the row, so the ones past its clipped edge peek into view.
    const timer = window.setTimeout(() => {
      if (row.scrollWidth <= row.clientWidth || row.scrollLeft > 0) return
      animations = Array.from(row.children).map((item) => item.animate(HINT_KEYFRAMES, HINT_DURATION_MS))
    }, HINT_DELAY_MS)
    const cancel = () => {
      window.clearTimeout(timer)
      animations.forEach((animation) => animation.cancel())
    }
    // A swipe already found the rest; bouncing under the finger would fight it.
    row.addEventListener('pointerdown', cancel, { once: true })

    return () => {
      cancel()
      row.removeEventListener('pointerdown', cancel)
    }
  }, [rowRef])
}
